import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';

export interface DunaOAuthClientsSecretDialogData {
  client_id: string;
  client_secret: string;
  title?: string;
}

@Component({
  selector: 'app-duna-oauth-clients-secret-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <h2 mat-dialog-title>{{ data.title || 'Gem client secret' }}</h2>
    <mat-dialog-content class="secret-content">
      <p class="warning">
        <mat-icon color="warn">warning</mat-icon>
        Client secret vises kun denne gang. Gem det sikkert (fx i SIM BFF-konfiguration).
      </p>

      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Client ID</mat-label>
        <input matInput [value]="data.client_id" readonly />
        <button
          mat-icon-button
          matSuffix
          type="button"
          aria-label="Kopiér client ID"
          (click)="copy(data.client_id, 'id')"
        >
          <mat-icon>content_copy</mat-icon>
        </button>
      </mat-form-field>

      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Client secret</mat-label>
        <input matInput [value]="data.client_secret" readonly />
        <button
          mat-icon-button
          matSuffix
          type="button"
          aria-label="Kopiér client secret"
          (click)="copy(data.client_secret, 'secret')"
        >
          <mat-icon>content_copy</mat-icon>
        </button>
      </mat-form-field>

      @if (copiedField) {
      <p class="copied-hint">Kopieret: {{ copiedField === 'id' ? 'Client ID' : 'Client secret' }}</p>
      }

      <mat-checkbox [(ngModel)]="confirmedSaved">
        Jeg har gemt secret
      </mat-checkbox>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-raised-button color="primary" [disabled]="!confirmedSaved" (click)="close()">
        Luk
      </button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      .secret-content {
        min-width: min(480px, 90vw);
      }
      .full-width {
        width: 100%;
      }
      .warning {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        margin-bottom: 16px;
      }
      .copied-hint {
        font-size: 0.875rem;
        color: var(--mat-sys-primary, #1976d2);
        margin: 0 0 8px;
      }
    `,
  ],
})
export class DunaOAuthClientsSecretDialogComponent {
  confirmedSaved = false;
  copiedField: 'id' | 'secret' | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DunaOAuthClientsSecretDialogData,
    private dialogRef: MatDialogRef<DunaOAuthClientsSecretDialogComponent>
  ) {}

  copy(value: string, field: 'id' | 'secret'): void {
    const text = value?.trim();
    if (!text) return;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        this.copiedField = field;
      });
      return;
    }
    const ta = document.createElement('textarea');
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    this.copiedField = field;
  }

  close(): void {
    this.dialogRef.close(true);
  }
}
